import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  X,
  Trash2,
  MoreHorizontal,
  Plus,
  Lock,
  Pencil,
  Check,
  ChevronDown,
  Eye,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import type {
  ProjectDoc,
  TaskDoc,
  RunDoc,
  ExecutionDoc,
  ExecutionStatus,
  ExecutionType,
  Role,
  WbsItemDoc,
} from '@mct/shared';
import { normalizeCatalogOptions, readTaskAreaIds } from '@mct/shared';
import { useAssignees } from '../../hooks/useFirestoreData';
import { makeExecutionId } from '@mct/shared';
import { AssigneeChipPicker } from '../common/AssigneeChipPicker';
import { AreaMultiPicker } from '../common/AreaMultiPicker';
import { buildLabelColorIndexMap } from '../../lib/areaColor';
import { ExecutionKindChip } from '../common/ExecutionKindChip';
import { BacklogLinkSection } from '../common/BacklogLinkSection';
import { Button } from '../ui/Button';
import { Input, Textarea, Label } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { Dialog } from '../ui/Dialog';
import {
  addBacklogUrl,
  postSuccessorNotes,
  removeBacklogUrl,
  setExecutionActualTime,
  setExecutionStatus,
  updateExecution,
  updateTask,
} from '../../lib/firestore';
import { getDb } from '../../lib/firebase';
import { recordExecutionEdit, recordTaskEdit } from '../../lib/undoStack';
import { usePreviousHandover } from '../../hooks/usePreviousHandover';
import { useViewportTier } from '../../lib/useViewportTier';
import {
  useTaskHistory,
  useBacklogCache,
  useExecutionsForRun,
  useRuns,
} from '../../hooks/useFirestoreData';
import { useWbsItems } from '../../hooks/useWbs';
import { useAuthState } from '../../hooks/useAuth';
import {
  AutosaveIndicator,
  useAutosaveStatus,
} from '../../hooks/useAutosaveStatus';
import { SIGNAL_COLOR, SIGNAL_LABEL, STATUS_OPTIONS } from '../DagView/signal';
import { statusPillClass, statusPillStyle } from '../../lib/statusColors';
import { buildBacklogIssueUrl, extractIssueKey } from '../../lib/backlog';
import { cn } from '../../lib/utils';
import {
  checkStatusTransition,
  type BlockedTransition,
} from '../../lib/statusGuard';

/** Debounce window for text-field autosave (タイトル / 説明 / 申し送り). */
const AUTOSAVE_DEBOUNCE_MS = 400;

/** Roles allowed to mutate task-level fields. Mirrors the predicate in
 *  `firestore.rules` (`hasRole(resource.data, ['owner','admin','editor'])`)
 *  so the UI surfaces a viewer banner / disables inputs instead of
 *  letting writes round-trip to a Rules denial. */
const TASK_EDIT_ROLES: ReadonlySet<Role> = new Set([
  'owner',
  'admin',
  'editor',
]);

/** Tooltip shown on the rollup-only `cycleDetected` flag — it's set by
 *  the Cloud Function and never user-editable. */
const ROLLUP_TOOLTIP_CYCLE =
  '自動計算 — Cloud Function が循環依存を検出した際に立てます';

/**
 * Preserve every newline (including consecutive blank lines) as a
 * visible line break in the rendered markdown.
 *
 * CommonMark collapses consecutive blank lines into a single paragraph
 * break — a 5-line gap in the textarea reads as one paragraph gap in
 * the preview, which surprises users who treat the editor as
 * what-you-see-is-what-you-get. We map empty source lines to a
 * non-breaking space (so they still take vertical space) and join
 * with the two-space hard-break sequence so every newline survives.
 *
 * Trade-off: trailing whitespace lands on every non-empty line in
 * the markdown source we hand to ReactMarkdown. That's harmless for
 * paragraphs/headings/lists and shows up as a couple of extra
 * characters inside fenced code blocks — acceptable for the prose
 * editors this panel exposes.
 */
function preserveLineBreaks(src: string): string {
  return src
    .split('\n')
    .map((line) => (line === '' ? ' ' : line))
    .join('  \n');
}

export function TaskDetailPanel({
  project,
  task,
  allTasks,
  currentRun,
  onClose,
  onDelete,
  onSelectTask,
  onUpdateDependsOn,
}: {
  project: ProjectDoc;
  task: TaskDoc;
  allTasks: readonly TaskDoc[];
  currentRun: RunDoc | null;
  onClose: () => void;
  onDelete: () => void;
  onSelectTask?: (taskId: string) => void;
  /** Commit new dependsOn for an arbitrary task (parent or child of current). */
  onUpdateDependsOn?: (taskId: string, nextDependsOn: string[]) => Promise<'ok' | 'cycle' | 'error'>;
}) {
  const { user } = useAuthState();
  const { status: saveStatus, run: runSave } = useAutosaveStatus();

  // Permission gate: only owner/admin/editor may mutate task fields
  // (mirrors the predicate in firestore.rules). When the role check
  // fails, every editor in the panel renders disabled with a tooltip
  // so viewer-tier users see a coherent read-only experience instead
  // of clicks that silently fail at the Rules layer.
  const role = user ? (task.memberRoles[user.uid] as Role | undefined) : undefined;
  const canEdit = Boolean(user) && Boolean(role && TASK_EDIT_ROLES.has(role));
  const { data: history } = useTaskHistory(project.projectId, task.taskId);
  const { data: backlogCache } = useBacklogCache(project.projectId);
  // Project-wide run list — needed for the per-phase actuals section so
  // we can iterate every phase (past, current, future) in `order` and
  // not just the ones with an Execution. `MetaSection` calls the same
  // hook independently; Firestore SDK dedupes the underlying snapshot
  // listener so there's no extra network cost.
  const { data: runs } = useRuns(project.projectId);
  // wbsItems を購読してフェーズ共通の completionCriteria を解決する。
  // currentRun.name と一致する `kind === 'phase'` の WBS 行を親フェーズ
  // として扱う — TaskDoc 側に parentPhaseId が無いため名前で結合する。
  const { data: wbsItems, loading: wbsLoading } = useWbsItems(project.projectId);
  const parentPhase = useMemo(() => {
    if (!currentRun) return null;
    return (
      wbsItems.find(
        (w) => w.kind === 'phase' && w.name === currentRun.name,
      ) ?? null
    );
  }, [wbsItems, currentRun]);
  const currentExec = history.find(
    (e) => currentRun && e.executionId === makeExecutionId(currentRun.runId, task.taskId),
  );

  // Run-level execution map, used by the status guard so we can refuse
  // a "start" / "skip" / "done" click when this task's predecessors
  // aren't `done`/`skipped` in the active phase. Reactively reflects
  // upstream status changes — once the predecessor is marked done, the
  // disabled options re-enable without a refresh.
  const { data: runExecs } = useExecutionsForRun(
    project.projectId,
    currentRun?.runId ?? null,
  );
  const execByTaskId = useMemo(() => {
    const m = new Map<string, ExecutionDoc>();
    for (const e of runExecs) m.set(e.taskId, e);
    return m;
  }, [runExecs]);
  const taskNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of allTasks) m.set(t.taskId, t.name);
    return m;
  }, [allTasks]);
  const validateStatusChange = useCallback(
    (next: ExecutionStatus): BlockedTransition | null => {
      if (!currentExec) return null;
      return checkStatusTransition(
        next,
        currentExec.status,
        task,
        execByTaskId,
        taskNameById,
      );
    },
    [currentExec, task, execByTaskId, taskNameById],
  );

  // Names of predecessors that aren't yet done/skipped in the current
  // run — used to surface a permanent, scannable hint under the pill so
  // users don't mistake the disabled menu for a bug. Only meaningful
  // when this row is still `not_started`; once started, transitions
  // are unrestricted by the guard so the hint goes away.
  const blockingPredecessorNames = useMemo<string[]>(() => {
    if (!currentExec || currentExec.status !== 'not_started') return [];
    // Probe via 'in_progress' (any forward status would yield the same
    // blocking set). Returns null when there are no blockers.
    const probe = checkStatusTransition(
      'in_progress',
      'not_started',
      task,
      execByTaskId,
      taskNameById,
    );
    if (!probe) return [];
    return probe.blockingPredecessorIds.map(
      (id) => taskNameById.get(id) ?? id,
    );
  }, [currentExec, task, execByTaskId, taskNameById]);

  // Transient banner for failed status-change attempts so the user
  // hears WHY their click was rejected, not just sees a disabled menu.
  const [statusGuardMsg, setStatusGuardMsg] = useState<string | null>(null);
  useEffect(() => {
    if (!statusGuardMsg) return;
    const t = window.setTimeout(() => setStatusGuardMsg(null), 3500);
    return () => window.clearTimeout(t);
  }, [statusGuardMsg]);

  // Task deletion funnels through a confirm modal (誤削除防止) — the
  // ⋯ header menu only opens this dialog; the actual `onDelete()` fires
  // from the dialog's danger button.
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data: prev } = usePreviousHandover(
    project.projectId,
    task.taskId,
    currentRun?.order ?? null,
  );

  // Successor tasks (same run) — the recipients shown in the "宛先" preview
  // for the SuccessorNotesEditor below. Empty when nothing in the project
  // depends on this task, in which case the editor surfaces a hint pointing
  // users to `handoverNotes` for cross-phase handoffs instead.
  const successorTasks = useMemo(
    () =>
      allTasks
        .filter((t) => t.dependsOn.includes(task.taskId))
        .map((t) => ({ taskId: t.taskId, name: t.name })),
    [allTasks, task.taskId],
  );

  // Posted handoffs from upstream tasks in the current run. For each
  // `dependsOn` predecessor we look up its execution in `execByTaskId`
  // (deterministic ID lookup, already loaded for the status guard) and
  // surface its `successorNotes` only after the author hit "投稿"
  // (= `successorNotesPostedAt` non-null). Drafts stay private.
  const predecessorPosts = useMemo(() => {
    if (!currentRun) return [];
    return task.dependsOn
      .map((predTaskId) => {
        const predExec = execByTaskId.get(predTaskId);
        if (!predExec) return null;
        // Legacy execution docs may not yet carry these fields — coerce
        // before reading so the banner just hides itself for them.
        const predPostedAt = predExec.successorNotesPostedAt ?? null;
        const predBody = predExec.successorNotes ?? '';
        if (!predPostedAt || !predBody.trim()) return null;
        return {
          taskId: predTaskId,
          taskName: predExec.taskName,
          postedAt: predPostedAt,
          body: predBody,
        };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
  }, [task.dependsOn, execByTaskId, currentRun]);

  // Phase 2.2 responsive: PC (`lg+`) keeps the resizable side panel; below
  // `lg` we promote the panel to a full-screen overlay (matches the WBS
  // detail-panel pattern from Phase 1). The panel-width style only
  // applies on desktop — on mobile/tablet we let CSS own the geometry so
  // the overlay covers the viewport.
  const tier = useViewportTier();
  const isDesktop = tier === 'desktop';

  // Mobile / tablet: Esc closes the panel — same gesture as the ✕ button
  // and the WBS overlay. Desktop side-panel users get the ✕ button only
  // (Esc on desktop is reserved for popovers like the StatusPill menu).
  useEffect(() => {
    if (isDesktop) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isDesktop, onClose]);

  // Resizable width (drag the left edge). Persisted in localStorage so
  // the user's preferred width survives reloads. Min 360 / max 1100 px
  // is wide enough for long task descriptions but won't eat the canvas.
  const [width, setWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return 560;
    const raw = window.localStorage.getItem('mct.taskDetailWidth');
    const n = raw ? Number(raw) : 560;
    return Number.isFinite(n) && n >= 360 && n <= 1100 ? n : 560;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('mct.taskDetailWidth', String(width));
  }, [width]);

  const beginResize = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = width;
      const onMove = (ev: MouseEvent) => {
        // Drag handle is on the LEFT edge — moving the pointer left
        // grows the panel, moving it right shrinks it.
        const delta = startX - ev.clientX;
        const next = Math.max(360, Math.min(1100, startW + delta));
        setWidth(next);
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [width],
  );

  return (
    <aside
      data-testid="task-detail-panel"
      // Inline width is desktop-only — on mobile/tablet the overlay
      // covers the viewport, so we let CSS classes own the geometry.
      style={isDesktop ? { width } : undefined}
      className={cn(
        'flex flex-col bg-white',
        // Mobile / tablet: full-viewport fixed overlay, lifted above
        // the rest of the page (z-50 to clear ReactFlow's z-30 pane).
        'fixed inset-0 z-50 h-screen w-full max-w-full',
        // Desktop (`lg+`): in-flow resizable side panel — matches the
        // pre-Phase-2.2 layout exactly.
        'lg:relative lg:inset-auto lg:z-auto lg:h-full lg:w-auto lg:max-w-none lg:shrink-0 lg:border-l lg:border-slate-200',
      )}
    >
      {/* Left-edge drag handle. Sits 3 px wide and is invisible at
          rest, so the panel border still reads as a thin grey line.
          Hover paints a subtle indigo wash to advertise drag-ability.
          Hidden below `lg` since the mobile overlay always fills the
          viewport — there's no width to drag. */}
      <div
        role="separator"
        aria-label="パネル幅をドラッグして変更"
        onMouseDown={beginResize}
        className="absolute left-0 top-0 z-10 hidden h-full w-1.5 -translate-x-1/2 cursor-col-resize hover:bg-indigo-300/40 lg:block"
      />

      <header className="border-b border-slate-200 px-4 py-2">
        <div className="flex items-start justify-between gap-2">
          {/* Status pill (left) + title (right) on one line. When no
              Execution is available we still render a same-sized
              placeholder pill so the title never shifts horizontally. */}
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {currentRun && currentRun.executionsReady && currentExec ? (
              <StatusPill
                value={currentExec.status}
                disabled={!user || !canEdit}
                validate={validateStatusChange}
                triggerHint={
                  blockingPredecessorNames.length > 0
                    ? `先行タスクが完了/スキップ済みになるまで、開始・完了・スキップに変更できません。`
                    : undefined
                }
                onChange={(next) => {
                  if (!user) return;
                  const block = validateStatusChange(next);
                  if (block) {
                    setStatusGuardMsg(block.message);
                    return;
                  }
                  const prevStatus = currentExec.status;
                  void setExecutionStatus(
                    getDb(),
                    project.projectId,
                    currentExec.runId,
                    currentExec.taskId,
                    user.uid,
                    next,
                  ).then(() => {
                    recordExecutionEdit({
                      projectId: project.projectId,
                      runId: currentExec.runId,
                      taskId: currentExec.taskId,
                      uid: user.uid,
                      before: { status: prevStatus } as ExecutionDoc,
                      patch: { status: next },
                      label: `"${task.name}" のステータス`,
                    });
                  });
                }}
              />
            ) : (
              <span
                data-testid="task-detail-status-placeholder"
                title={
                  !currentRun
                    ? 'フェーズが選択されていません'
                    : !currentRun.executionsReady
                    ? 'Execution を生成中です'
                    : 'このフェーズには Execution がまだありません'
                }
                className="inline-flex shrink-0 items-center rounded-full border border-dashed border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-400"
              >
                {!currentRun
                  ? 'フェーズ未選択'
                  : !currentRun.executionsReady
                  ? 'Execution 生成中…'
                  : 'Execution なし'}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <InlineTitleEditor
                value={task.name}
                canEdit={canEdit}
                onCommit={async (next) => {
                  const trimmed = next.trim();
                  if (!trimmed || trimmed === task.name) return;
                  if (!user) return;
                  await runSave(() =>
                    updateTask(getDb(), project.projectId, task.taskId, user.uid, {
                      name: trimmed,
                    }),
                  );
                  recordTaskEdit({
                    projectId: project.projectId,
                    taskId: task.taskId,
                    uid: user.uid,
                    before: task,
                    patch: { name: trimmed },
                    label: `"${task.name}" の名称`,
                  });
                }}
              />
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <AutosaveIndicator
              status={saveStatus}
              testId="task-detail-save-status"
            />
            {!canEdit ? (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] text-slate-500"
                title="このプロジェクトでは閲覧権限のみ — 編集できません"
                data-testid="task-detail-readonly-badge"
              >
                <Eye className="h-3 w-3" />
                閲覧のみ
              </span>
            ) : null}
            <TaskActionsMenu
              onDeleteSelect={() => setShowDeleteConfirm(true)}
            />
            <Button variant="ghost" className="!px-2" onClick={onClose} aria-label="閉じる">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Task internal id (TT-NNNN for xlsx-derived tasks) moved into
            MetaSection — see the `No` row in the タスク共通 properties
            grid below. Keeps the header row clean (status + title only). */}

        {/*
         * Persistent why-can't-I hint: shown the moment the row has
         * unfinished predecessors, *before* the user clicks anything.
         * Lists up to 3 names inline so it's scannable; the rest
         * collapse into "ほか N 件". Without this, the disabled menu
         * reads as a bug ("clicking does nothing"). Lives in the header
         * now that the pill moved up next to the title.
         */}
        {blockingPredecessorNames.length > 0 ? (
          <div className="mt-2 flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800">
            <Lock className="mt-0.5 h-3 w-3 shrink-0" />
            <div className="leading-snug">
              <div className="font-medium">
                まだ着手できません — 先行タスクの完了が必要です
              </div>
              <div className="mt-0.5 text-amber-700">
                {blockingPredecessorNames.slice(0, 3).join(' / ')}
                {blockingPredecessorNames.length > 3
                  ? ` ほか ${blockingPredecessorNames.length - 3} 件`
                  : ''}
              </div>
            </div>
          </div>
        ) : null}
        {statusGuardMsg ? (
          <p className="mt-2 text-xs text-amber-700">{statusGuardMsg}</p>
        ) : null}
      </header>

      <TaskDeleteConfirmDialog
        open={showDeleteConfirm}
        taskName={task.name}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          setShowDeleteConfirm(false);
          onDelete();
        }}
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 text-sm">
        {task.cycleDetected ? (
          <div
            className="mb-3 flex items-start gap-1.5 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700"
            title={ROLLUP_TOOLTIP_CYCLE}
            data-testid="task-detail-rollup-cycle"
            data-rollup="true"
          >
            <Lock className="mt-0.5 h-3 w-3 shrink-0" aria-label="自動計算" />
            <span>
              ⚠️ このタスクは循環依存の一部です: {task.cycleMembers.join(', ')}
            </span>
          </div>
        ) : null}

        <SectionBand
          variant="common"
          label="タスク共通"
          hint="全フェーズで同じ内容"
        >
          <MetaSection
            task={task}
            project={project}
            allTasks={allTasks}
            canEdit={canEdit}
            runSave={runSave}
          />

          <DependencySection
            task={task}
            allTasks={allTasks}
            canEdit={canEdit}
            onSelectTask={onSelectTask}
            onUpdateDependsOn={onUpdateDependsOn}
          />

          <DescriptionField
            task={task}
            uid={user?.uid ?? null}
            canEdit={canEdit}
            projectId={project.projectId}
            runSave={runSave}
          />
        </SectionBand>

        {runs.length > 0 ? (
          <SectionBand
            variant="phase"
            label={
              currentRun
                ? `現在フェーズ：${currentRun.name}`
                : 'フェーズ別記録'
            }
            hint={
              currentRun
                ? 'このフェーズだけに記録されます'
                : 'フェーズごとに記録されます'
            }
          >
            {prev && prev.handoverNotes ? (
              <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2">
                <div className="text-xs font-semibold text-amber-800">
                  前フェーズからの申し送り
                </div>
                <div className="prose prose-sm mt-1 max-w-none text-amber-900">
                  <ReactMarkdown remarkPlugins={[remarkBreaks]}>
                    {preserveLineBreaks(prev.handoverNotes)}
                  </ReactMarkdown>
                </div>
              </div>
            ) : null}

            {predecessorPosts.length > 0 ? (
              <div
                data-testid="task-detail-predecessor-posts"
                className="mb-3 space-y-2"
              >
                {predecessorPosts.map((p) => (
                  <div
                    key={p.taskId}
                    className="rounded-md border border-sky-300 bg-sky-50 px-3 py-2"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="text-xs font-semibold text-sky-800">
                        先行タスクからの申し送り · {p.taskName}
                      </div>
                      <div className="text-[11px] tabular-nums text-sky-700">
                        {format(p.postedAt.toDate(), 'MM/dd HH:mm')}
                      </div>
                    </div>
                    <div className="prose prose-sm mt-1 max-w-none text-sky-900">
                      <ReactMarkdown remarkPlugins={[remarkBreaks]}>
                        {preserveLineBreaks(p.body)}
                      </ReactMarkdown>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            <PhaseCompletionCriteriaReadOnly
              projectId={project.projectId}
              currentRun={currentRun}
              parentPhase={parentPhase}
              wbsLoading={wbsLoading}
            />

            <PhaseBacklogSection
              projectId={project.projectId}
              runs={runs}
              history={history}
              currentRun={currentRun}
              uid={user?.uid ?? null}
              canEdit={canEdit}
              runSave={runSave}
              backlogCache={backlogCache}
              spaceUrl={project.backlog?.spaceUrl ?? null}
            />

            {!currentRun ? null : !currentRun.executionsReady ? null : currentExec ? (
              <ExecutionEditor
                projectId={project.projectId}
                exec={currentExec}
                uid={user?.uid ?? null}
                canEdit={canEdit}
                runSave={runSave}
                successorTasks={successorTasks}
              />
            ) : null}

            <PhaseActualsSection
              projectId={project.projectId}
              runs={runs}
              history={history}
              currentRun={currentRun}
              uid={user?.uid ?? null}
              canEdit={canEdit}
              runSave={runSave}
            />
          </SectionBand>
        ) : null}

        <HistoryList history={history} currentRunId={currentRun?.runId ?? null} />
      </div>
    </aside>
  );
}

function ExecutionEditor({
  projectId,
  exec,
  uid,
  canEdit,
  runSave,
  successorTasks,
}: {
  projectId: string;
  exec: ExecutionDoc;
  uid: string | null;
  canEdit: boolean;
  runSave: (op: () => Promise<void>) => Promise<void>;
  /** Tasks in the same project whose `dependsOn` includes this task —
   *  shown as the "宛先" preview for `successorNotes`. Empty array means
   *  no downstream readers exist in this phase. */
  successorTasks: readonly { taskId: string; name: string }[];
}) {
  const [handover, setHandover] = useState(exec.handoverNotes);
  // Debounce timer for the markdown textarea. Same pattern as the WBS
  // description field — keystrokes only hit Firestore after a 400ms pause
  // so we don't write on every character.
  const handoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handoverPendingRef = useRef<string | null>(null);
  // Last-persisted value — the undo "before" target for the next flush.
  const handoverSyncedRef = useRef(exec.handoverNotes);

  const flushHandover = useCallback(() => {
    if (handoverTimerRef.current) {
      clearTimeout(handoverTimerRef.current);
      handoverTimerRef.current = null;
    }
    const pending = handoverPendingRef.current;
    handoverPendingRef.current = null;
    if (pending === null || !uid) return;
    if (pending === handoverSyncedRef.current) return;
    const before = handoverSyncedRef.current;
    handoverSyncedRef.current = pending;
    void runSave(() =>
      updateExecution(getDb(), projectId, exec.runId, exec.taskId, uid, {
        handoverNotes: pending,
      }),
    );
    recordExecutionEdit({
      projectId,
      runId: exec.runId,
      taskId: exec.taskId,
      uid,
      before: { handoverNotes: before } as ExecutionDoc,
      patch: { handoverNotes: pending },
      label: '申し送りメモ',
    });
  }, [exec.runId, exec.taskId, projectId, runSave, uid]);

  // When the execution row switches under us (or the canonical value
  // changes from the server), flush any pending typing burst first so it
  // hits the right doc, then reset the local draft.
  useEffect(() => {
    flushHandover();
    setHandover(exec.handoverNotes);
    handoverSyncedRef.current = exec.handoverNotes;
    // We intentionally depend only on the doc identity / canonical values —
    // re-running on every flush identity churn would defeat the reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exec.executionId, exec.handoverNotes]);

  // On unmount (panel close / row change), flush any in-flight typing.
  useEffect(() => {
    return () => {
      flushHandover();
    };
  }, [flushHandover]);

  return (
    <div className="space-y-4">
      {exec.status === 'done' && exec.completedAt ? (
        <div className="text-xs text-slate-500">
          ✅ 完了:{' '}
          <span className="tabular-nums">
            {format(exec.completedAt.toDate(), 'yyyy/MM/dd HH:mm')}
          </span>
        </div>
      ) : null}

      <MarkdownField
        key={`${exec.executionId}-handover`}
        value={handover}
        canEdit={canEdit}
        label="次フェーズへの申し送り (markdown)"
        testId="task-detail-exec-handover"
        emptyHint="クリックして申し送りを追加…"
        onChange={(next) => {
          setHandover(next);
          handoverPendingRef.current = next;
          if (handoverTimerRef.current) clearTimeout(handoverTimerRef.current);
          handoverTimerRef.current = setTimeout(
            flushHandover,
            AUTOSAVE_DEBOUNCE_MS,
          );
        }}
        onFlush={flushHandover}
      />

      <SuccessorNotesEditor
        projectId={projectId}
        exec={exec}
        uid={uid}
        canEdit={canEdit}
        runSave={runSave}
        successorTasks={successorTasks}
      />
    </div>
  );
}

/**
 * Intra-run handoff editor: a markdown draft aimed at successor tasks
 * within the SAME phase. Draft autosaves on every keystroke (same
 * debounce as `notes` / `handoverNotes`); hitting "投稿" stamps
 * `successorNotesPostedAt` so downstream readers see the message in
 * their "先行タスクからの申し送り" banner.
 *
 * Disabled-state matrix:
 *   - canEdit false               → all controls read-only
 *   - successorTasks empty        → editor visible but post button hidden,
 *                                   with hint pointing to handoverNotes
 *   - draft empty / unchanged-since-last-post → 投稿 button disabled
 */
function SuccessorNotesEditor({
  projectId,
  exec,
  uid,
  canEdit,
  runSave,
  successorTasks,
}: {
  projectId: string;
  exec: ExecutionDoc;
  uid: string | null;
  canEdit: boolean;
  runSave: (op: () => Promise<void>) => Promise<void>;
  successorTasks: readonly { taskId: string; name: string }[];
}) {
  // Execution docs predating the successorNotes schema field land here
  // with `undefined`; coerce to '' at the boundary so every downstream
  // read (draft state, comparisons, .trim()) sees a string.
  const canonicalDraft = exec.successorNotes ?? '';
  const postedAt = exec.successorNotesPostedAt ?? null;
  const [draft, setDraft] = useState(canonicalDraft);
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftPendingRef = useRef<string | null>(null);
  // Last-persisted value — the undo "before" target for the next flush.
  const draftSyncedRef = useRef(canonicalDraft);
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);

  const flushDraft = useCallback(() => {
    if (draftTimerRef.current) {
      clearTimeout(draftTimerRef.current);
      draftTimerRef.current = null;
    }
    const pending = draftPendingRef.current;
    draftPendingRef.current = null;
    if (pending === null || !uid) return;
    if (pending === draftSyncedRef.current) return;
    const before = draftSyncedRef.current;
    draftSyncedRef.current = pending;
    void runSave(() =>
      updateExecution(getDb(), projectId, exec.runId, exec.taskId, uid, {
        successorNotes: pending,
      }),
    );
    recordExecutionEdit({
      projectId,
      runId: exec.runId,
      taskId: exec.taskId,
      uid,
      before: { successorNotes: before } as ExecutionDoc,
      patch: { successorNotes: pending },
      label: '後継メモ',
    });
  }, [exec.runId, exec.taskId, projectId, runSave, uid]);

  useEffect(() => {
    flushDraft();
    setDraft(canonicalDraft);
    draftSyncedRef.current = canonicalDraft;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exec.executionId, canonicalDraft]);

  useEffect(() => {
    return () => {
      flushDraft();
    };
  }, [flushDraft]);

  const handlePost = useCallback(async () => {
    if (!uid || !canEdit || posting) return;
    if (!draft.trim()) return;
    if (successorTasks.length === 0) return;
    setPosting(true);
    setPostError(null);
    try {
      // Flush any pending debounce first so the published body matches
      // the displayed draft, then stamp postedAt via the dedicated helper.
      flushDraft();
      await postSuccessorNotes(
        getDb(),
        projectId,
        exec.runId,
        exec.taskId,
        uid,
        draft,
      );
    } catch (err) {
      console.error('postSuccessorNotes failed', err);
      setPostError(err instanceof Error ? err.message : String(err));
    } finally {
      setPosting(false);
    }
  }, [
    canEdit,
    draft,
    exec.runId,
    exec.taskId,
    flushDraft,
    posting,
    projectId,
    successorTasks.length,
    uid,
  ]);

  const hasSuccessors = successorTasks.length > 0;
  const trimmedDraft = draft.trim();
  const lastPostedBody = postedAt ? canonicalDraft : '';
  const isUnchangedSincePost = Boolean(postedAt) && draft === lastPostedBody;
  const canPost =
    canEdit &&
    hasSuccessors &&
    !posting &&
    trimmedDraft.length > 0 &&
    !isUnchangedSincePost;

  return (
    <div
      data-testid="task-detail-successor-notes"
      className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-xs font-semibold text-slate-700">
          次タスクへの申し送り (このフェーズ内)
        </div>
        {hasSuccessors ? (
          <div
            data-testid="task-detail-successor-recipients"
            className="text-[11px] text-slate-600"
          >
            宛先:{' '}
            <span className="text-slate-800">
              {successorTasks.map((s) => s.name).join(', ')}
            </span>
          </div>
        ) : (
          <div className="text-[11px] text-slate-500">
            後続タスクがありません — フェーズを跨ぐ場合は「次フェーズへの申し送り」をご利用ください
          </div>
        )}
      </div>

      <div className="mt-2">
        <MarkdownField
          key={`${exec.executionId}-successor`}
          value={draft}
          canEdit={canEdit}
          label=""
          testId="task-detail-exec-successor"
          emptyHint="クリックして申し送りを追加…"
          onChange={(next) => {
            setDraft(next);
            draftPendingRef.current = next;
            if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
            draftTimerRef.current = setTimeout(flushDraft, AUTOSAVE_DEBOUNCE_MS);
          }}
          onFlush={flushDraft}
        />
      </div>

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <div className="text-[11px] text-slate-500">
          {postedAt ? (
            <>
              最終投稿:{' '}
              <span className="tabular-nums">
                {format(postedAt.toDate(), 'yyyy/MM/dd HH:mm')}
              </span>
              {isUnchangedSincePost ? null : (
                <span className="ml-2 text-amber-700">
                  下書きに未投稿の変更があります
                </span>
              )}
            </>
          ) : (
            <span>未投稿（下書き保存のみ）</span>
          )}
        </div>
        {hasSuccessors ? (
          <Button
            variant="secondary"
            className="!px-3 !py-1 text-xs"
            data-testid="task-detail-successor-post"
            disabled={!canPost}
            onClick={() => {
              void handlePost();
            }}
          >
            {posting ? '投稿中…' : postedAt ? '再投稿' : '投稿'}
          </Button>
        ) : null}
      </div>
      {postError ? (
        <p className="mt-1 text-xs text-red-600">{postError}</p>
      ) : null}
    </div>
  );
}

/**
 * Shared view-by-default / edit-on-focus markdown editor. Resting
 * state renders the markdown via ReactMarkdown; clicking (or pressing
 * Enter / Space) on the preview flips in the raw textarea, with the
 * caret auto-positioned at the end of the existing text. Blur flushes
 * any pending writes and returns to the preview.
 *
 * Parent owns the draft state, debounce timer, and persistence so each
 * call site routes writes through its own helper (updateTask vs
 * updateExecution). This component only owns the editing toggle + the
 * preview / placeholder / textarea chrome.
 *
 * Reset semantics: pass a unique `key` tied to the underlying doc
 * identity (taskId, executionId, etc.) so a row switch remounts the
 * component and drops the editing state — the new row opens in the
 * resting preview view.
 */
function MarkdownField({
  value,
  canEdit,
  label,
  testId,
  emptyHint,
  rows = 5,
  onChange,
  onFlush,
}: {
  value: string;
  canEdit: boolean;
  label: string;
  /**
   * Stable testid for the textarea. Preview / empty placeholder
   * derive their testids by appending `-preview` / `-empty`.
   */
  testId: string;
  emptyHint: string;
  rows?: number;
  onChange: (next: string) => void;
  onFlush: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!editing) return;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    const end = el.value.length;
    el.setSelectionRange(end, end);
  }, [editing]);

  // Auto-resize the textarea to fit its content so users see every
  // line of the description at once, with no inner scrollbar. Resetting
  // to `auto` before measuring `scrollHeight` lets the height shrink
  // again when content is deleted.
  useEffect(() => {
    if (!editing) return;
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [editing, value]);

  if (!editing) {
    const enterEdit = () => {
      if (!canEdit) return;
      setEditing(true);
    };
    const keyHandler = (e: React.KeyboardEvent) => {
      if (!canEdit) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        enterEdit();
      }
    };
    return (
      <div className="mb-3">
        {label ? <Label>{label}</Label> : null}
        {value.trim() ? (
          <div
            role={canEdit ? 'button' : undefined}
            tabIndex={canEdit ? 0 : undefined}
            data-testid={`${testId}-preview`}
            title={canEdit ? 'クリックで編集' : undefined}
            onClick={enterEdit}
            onKeyDown={keyHandler}
            className={cn(
              'prose prose-sm max-w-none rounded-md border border-slate-200 px-3 py-2',
              canEdit
                ? 'cursor-text hover:border-sky-300'
                : 'cursor-default',
            )}
          >
            <ReactMarkdown remarkPlugins={[remarkBreaks]}>
              {preserveLineBreaks(value)}
            </ReactMarkdown>
          </div>
        ) : (
          <div
            role={canEdit ? 'button' : undefined}
            tabIndex={canEdit ? 0 : undefined}
            data-testid={`${testId}-empty`}
            onClick={enterEdit}
            onKeyDown={keyHandler}
            className={cn(
              'rounded-md border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-400',
              canEdit
                ? 'cursor-text hover:border-sky-300 hover:text-slate-500'
                : 'cursor-default',
            )}
          >
            {canEdit ? emptyHint : '（未入力）'}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mb-3">
      {label ? <Label>{label}</Label> : null}
      <Textarea
        ref={textareaRef}
        value={value}
        rows={rows}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => {
          onFlush();
          setEditing(false);
        }}
      />
    </div>
  );
}

/**
 * Visual grouping band that labels a stretch of the detail panel as
 * either "タスク共通" (fields persisted on the Task doc, shared across
 * every phase) or "現在フェーズ：{name}" (fields persisted on the
 * Execution doc for the active run only). The distinction is otherwise
 * invisible from the UI — same panel, same autosave indicator — so the
 * band makes the data-model boundary scannable.
 *
 * Visual treatment: 2px left accent (slate for common, sky for phase)
 * + a small uppercase chip label at the top. Deliberately subdued so
 * it reads as a section divider, not a heavy card.
 */
function SectionBand({
  variant,
  label,
  hint,
  children,
}: {
  variant: 'common' | 'phase';
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  const isPhase = variant === 'phase';
  return (
    <section
      data-testid={`task-detail-band-${variant}`}
      className={cn(
        'mb-4 border-l-2 pl-3',
        isPhase ? 'border-sky-300' : 'border-slate-300',
      )}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide',
            isPhase
              ? 'bg-sky-100 text-sky-700'
              : 'bg-slate-100 text-slate-600',
          )}
        >
          {label}
        </span>
        {hint ? (
          <span className="text-[10px] text-slate-500">{hint}</span>
        ) : null}
      </div>
      {children}
    </section>
  );
}

/**
 * Click-to-edit title in the panel header. Resting state renders as
 * plain text (no underline) with only a subtle background wash on hover
 * to hint at edit-ability — the input-box affordance (border + ring) is
 * reserved for the editing state so the header reads cleanly until the
 * user actually clicks. Click swaps the label for an `<input>` that
 * commits on blur / Enter and cancels on Esc — same gesture as the WBS
 * detail panel's `InlineTitleEditor` (PR #29).
 *
 * Read-only mode (when `canEdit` is false) renders a plain `<h2>` with
 * no edit affordance and an explanatory tooltip — viewers see a static
 * title rather than a fake hover state that wouldn't commit.
 */
function InlineTitleEditor({
  value,
  canEdit,
  onCommit,
}: {
  value: string;
  canEdit: boolean;
  onCommit: (next: string) => void | Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset the draft whenever the task changes so switching between
  // selected rows doesn't keep stale text in the editor.
  useEffect(() => {
    setDraft(value);
    setEditing(false);
  }, [value]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  if (!canEdit) {
    return (
      <h2
        data-testid="task-detail-title"
        className="text-sm font-semibold leading-snug"
        title="閲覧権限のみ — 編集できません"
      >
        {value || '(無題)'}
      </h2>
    );
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        data-testid="task-detail-title-input"
        className="w-full truncate rounded border border-sky-300 bg-white px-1 py-0.5 text-sm font-semibold outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-200"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          void onCommit(draft);
          setEditing(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setDraft(value);
            setEditing(false);
          }
        }}
      />
    );
  }
  return (
    <h2
      data-testid="task-detail-title"
      className="-mx-1 cursor-text truncate rounded px-1 py-0.5 text-sm font-semibold leading-snug hover:bg-slate-100"
      onClick={() => setEditing(true)}
      title="クリックで編集"
    >
      {value || '(無題)'}
    </h2>
  );
}

/**
 * Editable 説明 (markdown) field. Owns the draft / debounce / autosave
 * lifecycle for `task.description` and delegates the view-by-default /
 * edit-on-focus chrome to {@link MarkdownField}.
 *
 * Save semantics mirror {@link WbsItemDetailPanel}'s `DescriptionField`:
 *   - 400ms debounce so we don't write on every keystroke.
 *   - Pending writes flush on unmount, on row-switch, and on blur.
 *   - The `key={task.taskId}` on the rendered `MarkdownField` remounts
 *     the child on row switch so the editing toggle naturally resets.
 */
function DescriptionField({
  task,
  uid,
  canEdit,
  projectId,
  runSave,
}: {
  task: TaskDoc;
  uid: string | null;
  canEdit: boolean;
  projectId: string;
  runSave: (op: () => Promise<void>) => Promise<void>;
}) {
  const [draft, setDraft] = useState(task.description);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<{
    projectId: string;
    taskId: string;
    value: string;
  } | null>(null);
  // The value last known to be persisted — the "before" snapshot a flush
  // records as the undo target. Reset whenever the canonical doc changes.
  const syncedRef = useRef(task.description);
  const taskNameRef = useRef(task.name);
  taskNameRef.current = task.name;

  const flushPending = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (!pending || !uid) return;
    if (pending.value === syncedRef.current) return;
    const before = syncedRef.current;
    syncedRef.current = pending.value;
    void runSave(() =>
      updateTask(getDb(), pending.projectId, pending.taskId, uid, {
        description: pending.value,
      }),
    );
    recordTaskEdit({
      projectId: pending.projectId,
      taskId: pending.taskId,
      uid,
      before: { description: before } as TaskDoc,
      patch: { description: pending.value },
      label: `"${taskNameRef.current}" の説明`,
    });
  }, [runSave, uid]);

  useEffect(() => {
    flushPending();
    setDraft(task.description);
    syncedRef.current = task.description;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.taskId, task.description]);

  useEffect(() => {
    return () => {
      flushPending();
    };
  }, [flushPending]);

  const onChange = (next: string) => {
    setDraft(next);
    pendingRef.current = {
      projectId,
      taskId: task.taskId,
      value: next,
    };
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flushPending, AUTOSAVE_DEBOUNCE_MS);
  };

  return (
    <div data-testid="task-detail-description">
      <MarkdownField
        key={task.taskId}
        value={draft}
        canEdit={canEdit}
        label="説明 (markdown)"
        testId="task-detail-description-input"
        emptyHint="クリックして説明を追加…"
        onChange={onChange}
        onFlush={flushPending}
      />
    </div>
  );
}

function HistoryList({
  history,
  currentRunId,
}: {
  history: readonly ExecutionDoc[];
  currentRunId: string | null;
}) {
  const past = history.filter((e) => e.runId !== currentRunId);
  if (past.length === 0) return null;
  return (
    <div className="mt-6">
      <h3 className="text-xs font-semibold text-slate-600">過去フェーズ履歴</h3>
      <ol className="mt-2 space-y-2">
        {past.map((e) => (
          <li
            key={e.executionId}
            className="rounded border border-slate-200 px-3 py-2 text-xs"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: SIGNAL_COLOR[e.status] }}
                />
                <span className="font-medium">フェーズ#{e.runOrder}</span>
                <span className="text-slate-500">{SIGNAL_LABEL[e.status]}</span>
              </div>
              {e.completedAt ? (
                <span className="text-slate-500">
                  {format(e.completedAt.toDate(), 'yyyy/MM/dd')}
                </span>
              ) : null}
            </div>
            {e.handoverNotes ? (
              <div className="mt-1 rounded bg-amber-50 px-2 py-1 text-amber-900">
                申し送り: {e.handoverNotes}
              </div>
            ) : null}
            {e.backlogIssueKeys.length > 0 ? (
              <div className="mt-1 flex flex-wrap gap-1">
                {e.backlogIssueKeys.map((k) => (
                  <Badge key={k} tone="blue">
                    {k}
                  </Badge>
                ))}
              </div>
            ) : null}
          </li>
        ))}
      </ol>
    </div>
  );
}

function DependencySection({
  task,
  allTasks,
  canEdit,
  onSelectTask,
  onUpdateDependsOn,
}: {
  task: TaskDoc;
  allTasks: readonly TaskDoc[];
  /** Permission gate from the parent panel. Even when an updater is
   *  threaded in, viewer-tier users still see a static list. */
  canEdit: boolean;
  onSelectTask?: (taskId: string) => void;
  onUpdateDependsOn?: (taskId: string, nextDependsOn: string[]) => Promise<'ok' | 'cycle' | 'error'>;
}) {
  const byId = useMemo(() => {
    const m = new Map<string, TaskDoc>();
    for (const t of allTasks) m.set(t.taskId, t);
    return m;
  }, [allTasks]);
  const parents = task.dependsOn
    .map((id) => byId.get(id))
    .filter((t): t is TaskDoc => Boolean(t));
  const children = allTasks.filter((t) => t.dependsOn.includes(task.taskId));
  const [feedback, setFeedback] = useState<string | null>(null);
  // Deleting a dependency is destructive and hard to notice once gone, so
  // every removal funnels through a confirm modal (タツノ運用で誤削除が多発).
  // `kind` selects the direction copy; `counterpart` is the other task in
  // the edge (the predecessor for 'parent', the successor for 'child').
  const [pendingDelete, setPendingDelete] = useState<{
    kind: 'parent' | 'child';
    counterpart: TaskDoc;
  } | null>(null);
  // `canEdit` is now the combined gate: the parent passes both
  // permission state (role check) AND the presence of an updater.
  const canMutate = canEdit && Boolean(onUpdateDependsOn);

  const removeParent = async (parentId: string) => {
    if (!onUpdateDependsOn) return;
    const next = task.dependsOn.filter((d) => d !== parentId);
    const res = await onUpdateDependsOn(task.taskId, next);
    if (res !== 'ok') setFeedback(res === 'cycle' ? '循環のため拒否' : '更新失敗');
  };
  const removeChild = async (child: TaskDoc) => {
    if (!onUpdateDependsOn) return;
    const next = child.dependsOn.filter((d) => d !== task.taskId);
    const res = await onUpdateDependsOn(child.taskId, next);
    if (res !== 'ok') setFeedback(res === 'cycle' ? '循環のため拒否' : '更新失敗');
  };
  const addParent = async (newParentId: string) => {
    if (!onUpdateDependsOn) return;
    if (!byId.has(newParentId)) return setFeedback('タスクが見つかりません');
    if (newParentId === task.taskId) return setFeedback('自分自身は指定できません');
    if (task.dependsOn.includes(newParentId)) return setFeedback('既に依存しています');
    const next = [...task.dependsOn, newParentId];
    const res = await onUpdateDependsOn(task.taskId, next);
    if (res !== 'ok') setFeedback(res === 'cycle' ? '循環のため拒否' : '更新失敗');
  };
  const addChild = async (newChildId: string) => {
    if (!onUpdateDependsOn) return;
    const child = byId.get(newChildId);
    if (!child) return setFeedback('タスクが見つかりません');
    if (child.taskId === task.taskId) return setFeedback('自分自身は指定できません');
    if (child.dependsOn.includes(task.taskId)) return setFeedback('既に依存しています');
    const next = [...child.dependsOn, task.taskId];
    const res = await onUpdateDependsOn(child.taskId, next);
    if (res !== 'ok') setFeedback(res === 'cycle' ? '循環のため拒否' : '更新失敗');
  };

  const confirmPendingDelete = async () => {
    if (!pendingDelete) return;
    if (pendingDelete.kind === 'parent') {
      await removeParent(pendingDelete.counterpart.taskId);
    } else {
      await removeChild(pendingDelete.counterpart);
    }
    setPendingDelete(null);
  };

  useEffect(() => {
    if (!feedback) return;
    const timer = setTimeout(() => setFeedback(null), 2500);
    return () => clearTimeout(timer);
  }, [feedback]);

  const Row = ({
    t,
    onRemove,
  }: {
    t: TaskDoc;
    onRemove?: () => void;
  }) => (
    // Title-only row — the leading `<code>` chip rendering the raw
    // taskId (e.g. "A-1-3") was removed in PR #32 to match the WBS
    // detail panel, where users found the synthetic key meaningless.
    // Status / kind icons can come back here in the future, but the
    // raw sortKey-style id never re-enters the dep list.
    <div className="group flex items-center gap-1 rounded px-2 py-1 text-xs hover:bg-slate-100">
      <button
        type="button"
        onClick={() => onSelectTask?.(t.taskId)}
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
      >
        <span className="truncate">{t.name}</span>
      </button>
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          className="rounded p-0.5 text-slate-400 opacity-0 hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
          aria-label="削除"
          title="この依存関係を削除"
        >
          <X className="h-3 w-3" />
        </button>
      ) : null}
    </div>
  );

  return (
    <details className="mb-3 rounded-md border border-slate-200 p-2" open>
      <summary className="cursor-pointer text-xs font-medium text-slate-600">
        依存関係 ({parents.length} ← / → {children.length})
      </summary>
      <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <div className="mb-1 text-[11px] font-medium uppercase text-slate-500">
            ← 先行タスク ({parents.length})
          </div>
          {parents.length === 0 ? (
            <div className="px-2 text-xs text-slate-400">なし</div>
          ) : (
            <div className="flex flex-col">
              {parents.map((t) => (
                <Row
                  key={t.taskId}
                  t={t}
                  onRemove={
                    canMutate
                      ? () => setPendingDelete({ kind: 'parent', counterpart: t })
                      : undefined
                  }
                />
              ))}
            </div>
          )}
          {canMutate ? (
            <TaskPicker
              allTasks={allTasks}
              exclude={new Set([task.taskId, ...task.dependsOn])}
              onPick={addParent}
              placeholder="先行タスクを追加…"
            />
          ) : null}
        </div>
        <div>
          <div className="mb-1 text-[11px] font-medium uppercase text-slate-500">
            → 後続タスク ({children.length})
          </div>
          {children.length === 0 ? (
            <div className="px-2 text-xs text-slate-400">なし</div>
          ) : (
            <div className="flex flex-col">
              {children.map((t) => (
                <Row
                  key={t.taskId}
                  t={t}
                  onRemove={
                    canMutate
                      ? () => setPendingDelete({ kind: 'child', counterpart: t })
                      : undefined
                  }
                />
              ))}
            </div>
          )}
          {canMutate ? (
            <TaskPicker
              allTasks={allTasks}
              exclude={
                new Set([task.taskId, ...children.map((c) => c.taskId)])
              }
              onPick={addChild}
              placeholder="後続タスクを追加…"
            />
          ) : null}
        </div>
      </div>
      {feedback ? (
        <div className="mt-2 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
          {feedback}
        </div>
      ) : null}
      <DependencyDeleteConfirmDialog
        pending={pendingDelete}
        selfTaskName={task.name}
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmPendingDelete}
      />
    </details>
  );
}

/**
 * Confirm modal gating every dependency removal in the detail panel.
 * Mirrors the {@link SeedPhaseDialog} pattern (local `busy` state, danger
 * confirm button, Dialog chrome). Renders nothing until `pending` is set —
 * `Dialog` itself returns null while closed, but the body still references
 * `pending`, so all reads are optional-chained to survive the closed frame.
 */
function DependencyDeleteConfirmDialog({
  pending,
  selfTaskName,
  onCancel,
  onConfirm,
}: {
  pending: { kind: 'parent' | 'child'; counterpart: TaskDoc } | null;
  selfTaskName: string;
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
}) {
  const [busy, setBusy] = useState(false);
  const isParent = pending?.kind === 'parent';
  const title = isParent
    ? '先行タスクの依存を削除しますか？'
    : '後続タスクの依存を削除しますか？';
  const counterpartName = pending?.counterpart.name ?? '';
  // Edge direction is always predecessor → successor. For a 先行 (parent)
  // removal the counterpart is the predecessor; for a 後続 (child) removal
  // the counterpart is the successor.
  const fromName = isParent ? counterpartName : selfTaskName;
  const toName = isParent ? selfTaskName : counterpartName;
  return (
    <Dialog
      open={pending !== null}
      onClose={busy ? () => undefined : onCancel}
      title={title}
      className="max-w-md"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={busy}
            data-testid="dep-delete-cancel"
          >
            キャンセル
          </Button>
          <Button
            variant="danger"
            data-testid="dep-delete-confirm"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm();
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? '削除中…' : '削除'}
          </Button>
        </>
      }
    >
      <div
        className="space-y-3 text-sm text-slate-700"
        data-testid="dep-delete-body"
      >
        <p>以下の依存関係を削除します。この操作は元に戻せません。</p>
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
          <span className="font-semibold text-slate-800">{fromName}</span>
          <span className="text-slate-400">→</span>
          <span className="font-semibold text-slate-800">{toName}</span>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * "⋯" overflow menu in the panel header — replaces the bare trash
 * button. Opening it reveals destructive task-level actions (currently
 * just タスク削除). Selecting 削除 doesn't delete directly; it bubbles
 * up via `onDeleteSelect` so the parent can open its confirm modal.
 *
 * Self-contained popover following the same outside-click / Escape
 * close pattern as {@link StatusPill} / {@link CategorySelector} — the
 * project has no Radix dependency, so menus are hand-rolled.
 */
function TaskActionsMenu({
  onDeleteSelect,
}: {
  onDeleteSelect: () => void;
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (!el.closest('[data-task-actions-root]')) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  return (
    <div className="relative inline-block" data-task-actions-root>
      <Button
        variant="ghost"
        className="!px-2"
        aria-label="その他の操作"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="task-detail-actions-trigger"
        onClick={() => setOpen((v) => !v)}
      >
        <MoreHorizontal className="h-4 w-4" />
      </Button>
      {open ? (
        <div
          role="menu"
          data-testid="task-detail-actions-menu"
          className="absolute right-0 top-full z-40 mt-1 w-44 overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            data-testid="task-detail-action-delete"
            onClick={() => {
              setOpen(false);
              onDeleteSelect();
            }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
            タスク削除
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Confirm modal gating task deletion from the ⋯ header menu. Mirrors
 * the {@link DependencyDeleteConfirmDialog} pattern (local `busy` state,
 * danger confirm button, shared `Dialog` chrome). `onConfirm` is the
 * real `onDelete()` — the undo stack lets users recover with Cmd+Z, so
 * the body says as much rather than the harsher "元に戻せません".
 */
function TaskDeleteConfirmDialog({
  open,
  taskName,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  taskName: string;
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog
      open={open}
      onClose={busy ? () => undefined : onCancel}
      title="このタスクを削除しますか？"
      className="max-w-md"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={busy}
            data-testid="task-delete-cancel"
          >
            キャンセル
          </Button>
          <Button
            variant="danger"
            data-testid="task-delete-confirm"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm();
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? '削除中…' : '削除'}
          </Button>
        </>
      }
    >
      <div
        className="space-y-3 text-sm text-slate-700"
        data-testid="task-delete-body"
      >
        <p>
          「{taskName}」を削除します。この操作は Cmd+Z で元に戻せます。
        </p>
      </div>
    </Dialog>
  );
}

function TaskPicker({
  allTasks,
  exclude,
  onPick,
  placeholder,
}: {
  allTasks: readonly TaskDoc[];
  exclude: ReadonlySet<string>;
  onPick: (taskId: string) => void | Promise<void>;
  placeholder: string;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const matches = useMemo(() => {
    const qLower = q.trim().toLowerCase();
    if (!qLower) return [];
    return allTasks
      .filter((t) => !exclude.has(t.taskId))
      .filter(
        (t) =>
          t.taskId.toLowerCase().includes(qLower) ||
          t.name.toLowerCase().includes(qLower),
      )
      .slice(0, 8);
  }, [q, allTasks, exclude]);

  return (
    <div className="relative mt-1">
      <input
        type="text"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        className="input !h-7 !py-1 !text-xs"
      />
      {open && matches.length > 0 ? (
        <div className="absolute left-0 right-0 top-full z-10 mt-0.5 max-h-48 overflow-auto rounded-md border border-slate-200 bg-white shadow-md">
          {matches.map((t) => (
            <button
              key={t.taskId}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                void onPick(t.taskId);
                setQ('');
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 px-2 py-1 text-left text-xs hover:bg-slate-100"
            >
              {/* Same drop-the-taskId rule as the dep-row above —
                  picker results show only the human name. */}
              <span className="truncate">{t.name}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

type ImportedTask = TaskDoc & {
  applicableRunLabels?: string[];
  sourceScenario?: string | null;
  sourceCase?: string | null;
  sourceSheet?: string | null;
  sourceRow?: number | null;
  sysFrom?: string | null;
  sysTo?: string | null;
  area?: string | null;
  subSteps?: Array<{ content: string; sourceRow: number }>;
};

function MetaSection({
  task,
  project,
  allTasks,
  canEdit,
  runSave,
}: {
  task: TaskDoc;
  project: ProjectDoc;
  allTasks: readonly TaskDoc[];
  /** When false, every editor in this section disables itself with an
   *  explanatory tooltip — mirrors the gate enforced by firestore.rules. */
  canEdit: boolean;
  runSave: (op: () => Promise<void>) => Promise<void>;
}) {
  const t = task as ImportedTask;
  const { user } = useAuthState();
  const { data: assignees } = useAssignees(project.projectId);
  const { data: runs } = useRuns(project.projectId);

  // Distinct, ordered category list across the whole project. Used as
  // the dropdown options for the CategorySelector. New categories are
  // added by the user via the inline "+ 新規追加" path; once a task
  // adopts the new value it shows up here on subsequent reads.
  const categoryOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const t of allTasks) {
      const c = (t.category ?? '').trim();
      if (!c) continue;
      if (seen.has(c)) continue;
      seen.add(c);
      out.push(c);
    }
    out.sort((a, b) => a.localeCompare(b, 'ja'));
    return out;
  }, [allTasks]);

  // Distinct サブカテゴリ (original 大項目 表記) values across the project,
  // surfaced as dropdown suggestions for the second category field. Same
  // dynamic-collection approach as `categoryOptions` — no hardcoded list.
  const subcategoryOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const t of allTasks) {
      const c = (t.subcategory ?? '').trim();
      if (!c) continue;
      if (seen.has(c)) continue;
      seen.add(c);
      out.push(c);
    }
    out.sort((a, b) => a.localeCompare(b, 'ja'));
    return out;
  }, [allTasks]);

  // All field-level writes funnel through the panel-wide autosave
  // indicator so the user gets one coherent "保存中… / 保存済み ✓"
  // experience whether they're toggling a chip, picking a phase label,
  // or typing in the description textarea.
  const persistField = async (patch: Partial<TaskDoc>): Promise<void> => {
    if (!user || !canEdit) return;
    await runSave(() =>
      updateTask(getDb(), project.projectId, task.taskId, user.uid, patch),
    );
    recordTaskEdit({
      projectId: project.projectId,
      taskId: task.taskId,
      uid: user.uid,
      before: task,
      patch,
      label: `"${task.name}" の変更`,
    });
  };

  // Resolve the active assignee from the project's assignee list.
  // `assigneeId` is the canonical link; everything else (display name,
  // colour) flows from the matching `AssigneeDoc`.
  const currentAssignee = task.assigneeId
    ? assignees.find((a) => a.assigneeId === task.assigneeId) ?? null
    : null;

  // 領域 catalog comes from the same `project.wbsCatalog.area` source the
  // WBS tree uses (PR #25 / Phase 2.2). Reading it here lets the
  // migration-task picker render the exact same colour chips the WBS
  // tag row does — a label like "会計領域" reads identically across
  // surfaces. Auto-include any legacy free-text `area` value the import
  // path wrote so the picker can also clear it.
  const areaCatalog = useMemo(() => {
    const normalized = normalizeCatalogOptions(project.wbsCatalog?.area);
    const byLabel = new Map<string, { label: string; colorIndex?: number }>();
    for (const opt of normalized) {
      byLabel.set(opt.label, opt);
    }
    const legacyArea = (t.area ?? '').trim();
    if (legacyArea && !byLabel.has(legacyArea)) {
      byLabel.set(legacyArea, { label: legacyArea });
    }
    return Array.from(byLabel.values());
  }, [project.wbsCatalog?.area, t.area]);
  const areaOptions = useMemo(
    () => areaCatalog.map((o) => o.label),
    [areaCatalog],
  );
  const areaColorIndices = useMemo(
    () =>
      buildLabelColorIndexMap(
        areaCatalog.map((o) => ({ id: o.label, label: o.label, colorIndex: o.colorIndex })),
      ),
    [areaCatalog],
  );

  // Resolve the displayed 領域 list via the dual-read helper (canonical
  // `areaIds`, legacy `areaId` fallback). When the doc has neither but
  // carries a legacy import-only `area`, surface that single value so
  // pre-PR docs still show their 領域 until a write upgrades them.
  const currentAreas = useMemo(() => {
    const ids = readTaskAreaIds(task);
    if (ids.length > 0) return ids;
    const legacy = (t.area ?? '').trim();
    return legacy ? [legacy] : [];
  }, [task, t.area]);

  // Provenance fields (read-only) — these come from the Excel import
  // and represent where the task originated; mutating them would lie.
  const provenanceRows: Array<[string, React.ReactNode]> = [];
  if (t.sourceScenario) provenanceRows.push(['シナリオ', t.sourceScenario]);
  if (t.sysFrom || t.sysTo) {
    provenanceRows.push([
      '移行経路',
      <span className="text-slate-700">
        {t.sysFrom ?? '—'} <span className="text-slate-400">→</span> {t.sysTo ?? '—'}
      </span>,
    ]);
  }
  if (t.sourceSheet) provenanceRows.push(['元シート', t.sourceSheet]);
  if (t.sourceRow)
    provenanceRows.push([
      '元行',
      <code className="text-[11px] text-slate-500">#{t.sourceRow}</code>,
    ]);

  return (
    <div className="mb-4">
      {/* No card / details wrapper here — meta values render as a flat
          definition list directly inside the panel's main scroll area
          so the section reads as plain "task properties" instead of a
          collapsible drawer. */}
      <dl className="grid grid-cols-[7em_1fr] gap-x-3 gap-y-1.5 text-xs">
        {/* No — task identifier. For xlsx-derived タツノ tasks this is
            `TT-NNNN` (= Firestore doc id = sourceTaskId). Read-only. */}
        <dt className="text-slate-500">No</dt>
        <dd className="min-w-0">
          <code
            className="rounded bg-slate-100 px-1 py-0.5 font-mono text-slate-700"
            data-testid="task-detail-task-id"
          >
            {task.taskId}
          </code>
        </dd>

        {/* カテゴリ is now two stacked fields: the structured top-level
            category (curated set) and the original 大項目 表記 it was
            derived from (free text). The list / DAG surfaces only the
            top-level value; the source 表記 lives here for reference. */}
        <dt className="text-slate-500">カテゴリ</dt>
        <dd className="min-w-0">
          <CategorySelector
            value={task.category}
            options={categoryOptions}
            onCommit={(v) => persistField({ category: v })}
          />
        </dd>

        <dt className="text-slate-500">サブカテゴリ</dt>
        <dd className="min-w-0" data-testid="task-detail-subcategory">
          <CategorySelector
            value={task.subcategory ?? ''}
            options={subcategoryOptions}
            emptyLabel="なし"
            addLabel="サブカテゴリを追加"
            addPlaceholder="大項目の表記"
            // Empty subcategory persists as null (not '') so the schema's
            // `string | null` contract holds for hand-cleared values.
            onCommit={(v) => persistField({ subcategory: v.trim() === '' ? null : v })}
          />
        </dd>

        <dt className="text-slate-500">担当者</dt>
        <dd className="min-w-0">
          {/* Same color-chip picker the WBS detail panel uses (PR #25) —
              no more plain `<select>`. The hint about an empty assignee
              catalog is rendered separately so the picker itself stays
              compact even when 担当者 hasn't been seeded. */}
          <div className="space-y-1">
            <AssigneeChipPicker
              assignees={assignees}
              assigneeId={currentAssignee?.assigneeId ?? ''}
              currentAssignee={currentAssignee}
              label={null}
              testId="task-detail-assignee-picker"
              onChange={async (nextId) => {
                await persistField({ assigneeId: nextId === '' ? null : nextId });
              }}
            />
            {assignees.length === 0 ? (
              <div className="text-[11px] text-amber-600">
                担当者リストが空です。設定画面 →「担当者リスト」から追加してください。
              </div>
            ) : null}
          </div>
        </dd>

        <dt className="text-slate-500">領域</dt>
        <dd className="min-w-0">
          {/* Multi-value 領域 editor (mirrors Backlog's multi-category
              model). Catalog flows from `project.wbsCatalog.area` so
              choices and colours match the WBS surface exactly. */}
          <AreaMultiPicker
            value={currentAreas}
            options={areaOptions}
            colorIndices={areaColorIndices}
            label={null}
            onChange={async (next) => {
              await persistField({ areaIds: next });
            }}
          />
        </dd>

        <dt className="text-slate-500">実行方式</dt>
        <dd className="min-w-0">
          {/* Color-coded chip + dropdown (red / green / amber).
              Trigger width is fixed to the longest option (ハイブリッド)
              so swapping values doesn't shift the surrounding row. */}
          <ExecutionKindChip
            value={task.executionType}
            onChange={(v) => persistField({ executionType: v as ExecutionType })}
          />
        </dd>

        <dt className="text-slate-500">見積</dt>
        <dd className="min-w-0">
          <InlineNumberField
            value={task.estimatedMinutes}
            suffix="分"
            min={0}
            onCommit={(v) => persistField({ estimatedMinutes: v })}
          />
        </dd>

        <dt className="text-slate-500">対象フェーズ</dt>
        <dd className="min-w-0">
          <ApplicableRunsEditor
            runs={runs}
            value={task.applicableRunLabels ?? []}
            onCommit={(v) => persistField({ applicableRunLabels: v })}
          />
        </dd>

        {provenanceRows.map(([k, v]) => (
          <Fragment key={k}>
            <dt className="text-slate-500">{k}</dt>
            <dd className="min-w-0 break-words text-slate-800">{v}</dd>
          </Fragment>
        ))}
      </dl>
      {t.subSteps && t.subSteps.length > 0 ? (
        <div className="mt-3 border-t border-slate-100 pt-2">
          <div className="mb-1 text-[11px] font-medium uppercase text-slate-500">
            サブステップ ({t.subSteps.length})
          </div>
          <ol className="list-decimal space-y-2 pl-5 text-xs text-slate-700">
            {t.subSteps.map((s, i) => (
              <li key={i}>
                <pre className="whitespace-pre-wrap break-words font-sans">{s.content}</pre>
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </div>
  );
}

/* ----------------------------------------------------------------- */
/*  Asana-style status pill (top of the panel)                        */
/* ----------------------------------------------------------------- */

function StatusPill({
  value,
  disabled,
  validate,
  triggerHint,
  onChange,
}: {
  value: ExecutionStatus;
  disabled?: boolean;
  /**
   * Optional per-option gate. Returning a `BlockedTransition` for an
   * option dims it in the dropdown and shows the reason in its title;
   * the parent's onChange should still re-check before persisting.
   */
  validate?: (next: ExecutionStatus) => BlockedTransition | null;
  /**
   * Native-tooltip hint for the trigger pill itself. Used to explain
   * why the menu's options are disabled before the user even clicks.
   */
  triggerHint?: string;
  onChange: (next: ExecutionStatus) => void;
}) {
  const [open, setOpen] = useState(false);

  // Close on Escape / outside click.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (!el.closest('[data-status-pill-root]')) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  return (
    <div className="relative inline-block" data-status-pill-root>
      {/* The status word itself IS the pill — no leading dot. On
          hover (when editable) a ▼ slides in to hint at the dropdown;
          when disabled the affordance is hidden and the pill dims. */}
      <button
        type="button"
        data-status={value}
        disabled={disabled}
        title={triggerHint}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'group/status inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition-colors',
          statusPillClass(value),
          disabled ? 'cursor-not-allowed opacity-60' : 'hover:brightness-95',
        )}
      >
        <span>{SIGNAL_LABEL[value]}</span>
        {!disabled ? (
          <ChevronDown
            className="h-3 w-3 opacity-0 transition-opacity group-hover/status:opacity-60"
            aria-hidden
          />
        ) : null}
      </button>
      {open ? (
        <div className="absolute left-0 top-full z-40 mt-1 w-56 overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          {STATUS_OPTIONS.map((s) => {
            const active = s === value;
            const blocked = validate?.(s) ?? null;
            return (
              <button
                key={s}
                type="button"
                disabled={Boolean(blocked)}
                // Native title fires on long hover — pairs with the
                // visual lock icon so the user gets both immediate and
                // detail feedback. The persistent amber hint above the
                // pill carries the full why-message in plain sight.
                title={blocked?.message}
                onClick={() => {
                  if (blocked) return;
                  setOpen(false);
                  if (s !== value) onChange(s);
                }}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs',
                  blocked
                    ? 'cursor-not-allowed text-slate-400'
                    : 'hover:bg-slate-50',
                  active ? 'bg-slate-50 font-semibold' : '',
                )}
              >
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
                    statusPillClass(s),
                    blocked ? 'opacity-50' : '',
                  )}
                >
                  {statusPillStyle(s).label}
                </span>
                {blocked ? (
                  <Lock
                    className="ml-auto h-3 w-3 text-amber-500"
                    aria-label="先行タスク未完了"
                  />
                ) : active ? (
                  <Check className="ml-auto h-3 w-3 text-slate-500" />
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/* ----------------------------------------------------------------- */
/*  Inline editors used by MetaSection                                */
/* ----------------------------------------------------------------- */

function CategorySelector({
  value,
  options,
  onCommit,
  emptyLabel = '未分類',
  addLabel = '新しいカテゴリを追加',
  addPlaceholder = '新しいカテゴリ名',
}: {
  value: string;
  options: readonly string[];
  onCommit: (next: string) => Promise<void> | void;
  /** Label shown when the field is empty + as the "clear" menu row. */
  emptyLabel?: string;
  /** Label for the inline "add new value" affordance. */
  addLabel?: string;
  /** Placeholder for the new-value text input. */
  addPlaceholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');

  // Close on Escape / outside click.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setAdding(false);
        setDraft('');
      }
    };
    const onClickAway = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (!el.closest('[data-category-selector-root]')) {
        setOpen(false);
        setAdding(false);
        setDraft('');
      }
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClickAway);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClickAway);
    };
  }, [open]);

  const pick = async (next: string) => {
    setOpen(false);
    setAdding(false);
    setDraft('');
    await onCommit(next);
  };

  const commitNew = async () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      setAdding(false);
      return;
    }
    await pick(trimmed);
  };

  return (
    <div className="relative" data-category-selector-root>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="group flex w-full items-center justify-between gap-1 rounded px-1 py-0.5 text-left hover:bg-slate-100"
      >
        <span className={cn('truncate', !value && 'text-slate-400')}>
          {value || emptyLabel}
        </span>
        <ChevronDown className="h-3 w-3 shrink-0 text-slate-400" />
      </button>
      {open ? (
        <div className="absolute left-0 top-full z-40 mt-1 w-56 overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          <div className="max-h-56 overflow-y-auto">
            {/* Always-available clear option (未分類 / なし). */}
            <button
              type="button"
              onClick={() => void pick('')}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-slate-50',
                !value ? 'bg-slate-50 font-semibold' : '',
              )}
            >
              <span className="text-slate-400">{emptyLabel}</span>
              {!value ? (
                <Check className="ml-auto h-3 w-3 text-slate-500" />
              ) : null}
            </button>
            {options.map((c) => {
              const active = c === value;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => void pick(c)}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-slate-50',
                    active ? 'bg-slate-50 font-semibold' : '',
                  )}
                >
                  <span className="truncate">{c}</span>
                  {active ? (
                    <Check className="ml-auto h-3 w-3 text-slate-500" />
                  ) : null}
                </button>
              );
            })}
          </div>
          <div className="border-t border-slate-100">
            {!adding ? (
              <button
                type="button"
                onClick={() => setAdding(true)}
                className="flex w-full items-center gap-1 px-3 py-1.5 text-left text-xs text-indigo-600 hover:bg-indigo-50"
              >
                <Plus className="h-3 w-3" />
                {addLabel}
              </button>
            ) : (
              <div className="flex items-center gap-1 px-2 py-1.5">
                <Input
                  autoFocus
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void commitNew();
                    } else if (e.key === 'Escape') {
                      setAdding(false);
                      setDraft('');
                    }
                  }}
                  placeholder={addPlaceholder}
                  className="!py-0.5 !text-xs"
                />
                <button
                  type="button"
                  onClick={() => void commitNew()}
                  disabled={!draft.trim()}
                  aria-label="追加"
                  className="rounded p-1 text-emerald-600 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-30"
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function InlineNumberField({
  value,
  suffix,
  min,
  onCommit,
}: {
  value: number;
  suffix?: string;
  min?: number;
  onCommit: (next: number) => Promise<void> | void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  useEffect(() => {
    if (!editing) setDraft(String(value));
  }, [value, editing]);
  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="group flex w-full items-center justify-between gap-1 rounded px-1 py-0.5 text-left text-slate-800 hover:bg-slate-100"
      >
        <span className="tabular-nums">
          {value}
          {suffix ? <span className="text-slate-500"> {suffix}</span> : null}
        </span>
        <Pencil className="h-3 w-3 shrink-0 text-slate-300 group-hover:text-slate-500" />
      </button>
    );
  }
  const commit = () => {
    const n = Number(draft);
    const next = Number.isFinite(n) ? Math.max(min ?? -Infinity, n) : value;
    void Promise.resolve(onCommit(next)).finally(() => setEditing(false));
  };
  return (
    <div className="flex items-center gap-1">
      <Input
        autoFocus
        type="number"
        min={min}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            setEditing(false);
            setDraft(String(value));
          }
        }}
        className="!w-20 !py-0.5 !text-xs"
      />
      {suffix ? <span className="text-slate-500 text-[11px]">{suffix}</span> : null}
      <button
        type="button"
        onClick={commit}
        className="rounded p-1 text-emerald-600 hover:bg-emerald-50"
        aria-label="保存"
      >
        <Check className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function ApplicableRunsEditor({
  runs,
  value,
  onCommit,
}: {
  runs: readonly RunDoc[];
  value: readonly string[];
  onCommit: (next: string[]) => Promise<void> | void;
}) {
  // The data model stores LABELS (substrings) — a label like "リハ"
  // matches every run whose name contains that token. The editor
  // here exposes a flat list of run names; toggling adds/removes the
  // exact run name. (Substring labels from imports keep working
  // since matching is contains-based on the backend.)
  const all = runs.map((r) => r.name);
  const toggle = async (name: string) => {
    const set = new Set(value);
    if (set.has(name)) set.delete(name);
    else set.add(name);
    await onCommit(Array.from(set));
  };
  if (all.length === 0) {
    return <span className="text-slate-400 text-[11px]">フェーズ未定義</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {all.map((name) => {
        const active = value.some((v) => name.includes(v) || v === name);
        return (
          <button
            key={name}
            type="button"
            onClick={() => void toggle(name)}
            className={cn(
              'rounded-full border px-2 py-0.5 text-[11px] transition-colors',
              active
                ? 'border-blue-500 bg-blue-500 text-white'
                : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400',
            )}
          >
            {name}
          </button>
        );
      })}
      {value.length > 0 && all.every((n) => !value.includes(n)) ? (
        <span
          className="text-[10px] text-slate-400"
          title={`保持中: ${value.join(', ')}`}
        >
          (取込ラベル: {value.join(', ')})
        </span>
      ) : null}
    </div>
  );
}

/* ----------------------------------------------------------------- */
/*  実績時間（フェーズ別） — per-phase actual time tracking            */
/* ----------------------------------------------------------------- */

/**
 * Compact "Xh Ym" duration formatter. Returns "—" when either side of
 * the interval is missing or the result would be negative (a typo where
 * 終了 < 開始 is treated as "no value yet" so the table never shows a
 * confusing "-2h"). Rounds to the nearest minute.
 *
 * Exported only to siblings via `function` declaration — not part of
 * the panel's public surface; tests pin its shape via the source.
 */
function formatPhaseDuration(
  startAt: Date | null,
  endAt: Date | null,
): string {
  if (!startAt || !endAt) return '—';
  const totalMin = Math.round((endAt.getTime() - startAt.getTime()) / 60000);
  if (!Number.isFinite(totalMin) || totalMin <= 0) return '—';
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * Convert a JS Date to the local-time string `<input type="datetime-local">`
 * expects (`YYYY-MM-DDTHH:mm`). Returns `''` for null so the input stays
 * empty rather than NaN-formatted. Mirrors the WBS dateInput helper but
 * keeps minute precision (datetime-local omits seconds).
 */
function toDatetimeLocalValue(d: Date | null): string {
  if (!d) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/** Compact display: "3/15 09:00". Drops the year for table density. */
function formatPhaseWhen(d: Date | null): string {
  if (!d) return '—';
  return format(d, 'M/d HH:mm');
}

/**
 * Render an execution's per-task planned range (`plannedStartDate` /
 * `plannedEndDate`). Both are already canonical `YYYY-MM-DD` strings, so we
 * surface them verbatim. Returns "—" when neither is set (legacy executions
 * and runs whose tasks carry no per-task plan).
 */
function formatPlannedRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  if (!start && !end) return '—';
  return `${start ?? '—'} → ${end ?? '—'}`;
}

/**
 * Backlog 課題（フェーズ別） — sister component of PhaseActualsSection
 * that surfaces the Backlog issue list registered against every phase
 * for this task. The active phase exposes the full `BacklogLinkSection`
 * (add / remove via chip + datalist input); past and future phases
 * render their chips read-only so the user can scan the cross-phase
 * trail at a glance without losing track of which phase they're
 * currently editing.
 *
 *   - One row per Run (sorted by `order`) — independent of whether an
 *     Execution doc exists yet for this (run × task). Future phases
 *     stay visible so the table reads as a complete schedule.
 *   - Persistence still goes through `addBacklogUrl` / `removeBacklogUrl`
 *     against the active Execution doc — Backlog is intentionally kept
 *     phase-scoped so each phase can register its own issue set without
 *     the previous phase's links leaking forward.
 *
 * Renders nothing when the project has no runs (empty state stays quiet
 * — the meta section already hints "フェーズ未定義").
 */
/**
 * フェーズ共通の完了基準を read-only で表示する小さなセクション。
 * 親フェーズは `currentRun.name` と一致する `WbsItemDoc(kind === 'phase')`
 * を引き当てる — TaskDoc 自体には parentPhaseId が無いため名前で結合する。
 * 編集はここからは出来ず、WBS ページの該当フェーズ詳細パネルへ誘導する。
 *
 * 状態:
 *   - currentRun が無い → 何も描画しない（呼び出し側のガードと整合）。
 *   - WBS 読込中 → 軽量スケルトン。
 *   - 親フェーズが見つかる + criteria あり → 本文を whitespace-pre-wrap で表示。
 *   - 親フェーズは見つかるが criteria 未設定 → 薄字で「未設定」+ 設定リンク。
 *   - WBS にマッチが無い (リンクが切れている等) → 静かに非表示。
 */
function PhaseCompletionCriteriaReadOnly({
  projectId,
  currentRun,
  parentPhase,
  wbsLoading,
}: {
  projectId: string;
  currentRun: RunDoc | null;
  parentPhase: WbsItemDoc | null;
  wbsLoading: boolean;
}) {
  if (!currentRun) return null;

  if (wbsLoading && !parentPhase) {
    return (
      <div
        data-testid="task-detail-completion-criteria-skeleton"
        className="mb-3 h-10 animate-pulse rounded-md border border-slate-200 bg-slate-50"
      />
    );
  }

  if (!parentPhase) return null;

  const criteria = (parentPhase.completionCriteria ?? '').trim();
  const editUrl = `/projects/${projectId}/wbs?focus=${encodeURIComponent(parentPhase.itemId)}`;

  return (
    <section
      data-testid="task-detail-completion-criteria"
      className="mb-3 rounded-md border border-sky-200 bg-sky-50/60 px-3 py-2"
    >
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-sky-800">
          フェーズ共通の完了基準
        </h4>
        <Link
          to={editUrl}
          className="text-[11px] text-sky-700 hover:underline"
        >
          フェーズ画面で編集
        </Link>
      </div>
      {criteria ? (
        <p
          data-testid="task-detail-completion-criteria-body"
          className="mt-1 whitespace-pre-wrap text-xs text-slate-800"
        >
          {criteria}
        </p>
      ) : (
        <p
          data-testid="task-detail-completion-criteria-empty"
          className="mt-1 text-xs text-slate-400"
        >
          フェーズに完了基準が設定されていません
        </p>
      )}
    </section>
  );
}

function PhaseBacklogSection({
  projectId,
  runs,
  history,
  currentRun,
  uid,
  canEdit,
  runSave,
  backlogCache,
  spaceUrl,
}: {
  projectId: string;
  runs: readonly RunDoc[];
  history: readonly ExecutionDoc[];
  currentRun: RunDoc | null;
  uid: string | null;
  canEdit: boolean;
  runSave: (op: () => Promise<void>) => Promise<void>;
  backlogCache: readonly { issueKey: string; statusName: string; url: string; title: string }[];
  spaceUrl: string | null;
}) {
  const execByRunId = useMemo(() => {
    const m = new Map<string, ExecutionDoc>();
    for (const e of history) m.set(e.runId, e);
    return m;
  }, [history]);

  const sortedRuns = useMemo(
    () => [...runs].sort((a, b) => a.order - b.order),
    [runs],
  );

  if (sortedRuns.length === 0) return null;

  return (
    <details
      className="mb-3 rounded-md border border-slate-200 p-2"
      data-testid="task-detail-phase-backlog"
      open
    >
      <summary className="cursor-pointer text-xs font-medium text-slate-600">
        Backlog 課題（フェーズ別）
      </summary>
      <div className="mt-2 space-y-2">
        {sortedRuns.map((r) => {
          const isCurrent = currentRun?.runId === r.runId;
          return (
            <PhaseBacklogRow
              key={r.runId}
              run={r}
              exec={execByRunId.get(r.runId)}
              isCurrent={isCurrent}
              canEdit={canEdit && isCurrent}
              uid={uid}
              projectId={projectId}
              runSave={runSave}
              backlogCache={backlogCache}
              spaceUrl={spaceUrl}
            />
          );
        })}
      </div>
    </details>
  );
}

/**
 * Per-row Backlog editor. The current phase opens the full
 * `BacklogLinkSection`; non-current phases show a read-only chip
 * strip (or "—" when empty). Future phases without an Execution doc
 * yet show an explanatory hint identical to the actuals table so the
 * two surfaces gate generation the same way.
 */
function PhaseBacklogRow({
  run,
  exec,
  isCurrent,
  canEdit,
  uid,
  projectId,
  runSave,
  backlogCache,
  spaceUrl,
}: {
  run: RunDoc;
  exec: ExecutionDoc | undefined;
  isCurrent: boolean;
  canEdit: boolean;
  uid: string | null;
  projectId: string;
  runSave: (op: () => Promise<void>) => Promise<void>;
  backlogCache: readonly { issueKey: string; statusName: string; url: string; title: string }[];
  spaceUrl: string | null;
}) {
  const issueKeys = useMemo(() => {
    if (!exec) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const u of exec.backlogIssueUrls) {
      const k = extractIssueKey(u);
      if (k && !seen.has(k)) {
        seen.add(k);
        out.push(k);
      }
    }
    return out;
  }, [exec]);

  const rowClass = cn(
    'rounded-md border p-2',
    isCurrent ? 'border-sky-200 bg-sky-50/40' : 'border-slate-100',
  );

  return (
    <div
      data-testid="phase-backlog-row"
      data-run-id={run.runId}
      data-is-current={isCurrent ? 'true' : 'false'}
      className={rowClass}
    >
      <div className="mb-1.5 flex items-center gap-1.5">
        <span className="text-xs font-medium text-slate-800">{run.name}</span>
        {isCurrent ? (
          <span
            className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700"
            title="現在のフェーズ"
          >
            現在
          </span>
        ) : null}
      </div>
      {canEdit && exec ? (
        <BacklogLinkSection
          issueKeys={issueKeys}
          backlogCache={backlogCache}
          spaceUrl={spaceUrl}
          datalistId={`backlog-cache-exec-${exec.executionId}`}
          onAdd={async (key) => {
            if (!uid) return;
            const url = buildBacklogIssueUrl(spaceUrl, key) ?? `/view/${key}`;
            await runSave(() =>
              addBacklogUrl(
                getDb(),
                projectId,
                exec.runId,
                exec.taskId,
                uid,
                exec.backlogIssueUrls,
                url,
              ),
            );
          }}
          onRemove={async (key) => {
            if (!uid) return;
            const url = exec.backlogIssueUrls.find(
              (u) => extractIssueKey(u) === key,
            );
            if (!url) return;
            await runSave(() =>
              removeBacklogUrl(
                getDb(),
                projectId,
                exec.runId,
                exec.taskId,
                uid,
                exec.backlogIssueUrls,
                url,
              ),
            );
          }}
        />
      ) : issueKeys.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {issueKeys.map((k) => (
            <Badge key={k} tone="blue">
              {k}
            </Badge>
          ))}
        </div>
      ) : (
        <span className="text-xs text-slate-300">—</span>
      )}
      {isCurrent && !exec ? (
        <div className="mt-1 text-[10px] text-slate-400">
          Execution 未生成 — このフェーズの Backlog 入力は生成完了後に有効化されます
        </div>
      ) : null}
    </div>
  );
}

/**
 * 実績時間（フェーズ別） — read across every phase for this task and
 * surface its actual start / end timestamps. Past phases render
 * read-only; the active phase opens up as inline `<input type="datetime-local">`
 * controls when the user has edit permission. The intentional contract:
 *
 *   - One row per Run (sorted by `order`) — independent of whether an
 *     Execution doc exists yet for this (run × task).
 *   - The "current" row is determined by `currentRun.runId` from the
 *     parent — same source the status pill at the top uses, so the two
 *     surfaces never disagree about which phase is being edited.
 *   - Persistence reuses the existing Execution `startedAt` / `completedAt`
 *     fields (Case A in the design): the Execution doc already keys on
 *     `{runId}_{taskId}` so no schema change is needed.
 *   - The memo column reads the existing `notes` field truncated, so
 *     past-phase notes are scannable without duplicating the
 *     ExecutionEditor's full markdown textarea below.
 *
 * Renders nothing when the project has no runs (empty state stays quiet
 * — the meta section already hints "フェーズ未定義").
 */
function PhaseActualsSection({
  projectId,
  runs,
  history,
  currentRun,
  uid,
  canEdit,
  runSave,
}: {
  projectId: string;
  runs: readonly RunDoc[];
  history: readonly ExecutionDoc[];
  currentRun: RunDoc | null;
  uid: string | null;
  canEdit: boolean;
  runSave: (op: () => Promise<void>) => Promise<void>;
}) {
  const execByRunId = useMemo(() => {
    const m = new Map<string, ExecutionDoc>();
    for (const e of history) m.set(e.runId, e);
    return m;
  }, [history]);

  const sortedRuns = useMemo(
    () => [...runs].sort((a, b) => a.order - b.order),
    [runs],
  );

  if (sortedRuns.length === 0) return null;

  return (
    <details
      className="mb-3 mt-3 rounded-md border border-slate-200 p-2"
      data-testid="task-detail-phase-actuals"
      open
    >
      <summary className="cursor-pointer text-xs font-medium text-slate-600">
        実績時間（フェーズ別）
      </summary>
      <div className="mt-2 overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="text-left text-[11px] uppercase text-slate-500">
              <th className="px-2 py-1 font-medium">フェーズ</th>
              <th className="px-2 py-1 font-medium">計画日</th>
              <th className="px-2 py-1 font-medium">開始 → 終了</th>
              <th className="px-2 py-1 font-medium">所要時間</th>
            </tr>
          </thead>
          <tbody>
            {sortedRuns.map((r) => {
              const isCurrent = currentRun?.runId === r.runId;
              return (
                <PhaseActualRow
                  key={r.runId}
                  run={r}
                  exec={execByRunId.get(r.runId)}
                  isCurrent={isCurrent}
                  canEdit={canEdit && isCurrent}
                  uid={uid}
                  projectId={projectId}
                  runSave={runSave}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </details>
  );
}

/**
 * Per-row editor. Stays local-state-only for the in-flight typing burst
 * and flushes through `setExecutionActualTime` after a 400ms debounce.
 * The flush identity follows the executionId so a row swap (different
 * task selected) writes to the right doc.
 */
function PhaseActualRow({
  run,
  exec,
  isCurrent,
  canEdit,
  uid,
  projectId,
  runSave,
}: {
  run: RunDoc;
  exec: ExecutionDoc | undefined;
  isCurrent: boolean;
  /** Combined gate: only true when the parent allows editing AND the
   *  row is the currently-active phase. Past phases pass `false`. */
  canEdit: boolean;
  uid: string | null;
  projectId: string;
  runSave: (op: () => Promise<void>) => Promise<void>;
}) {
  const canonicalStart = exec?.startedAt?.toDate() ?? null;
  const canonicalEnd = exec?.completedAt?.toDate() ?? null;

  // Local drafts so typing in the datetime input doesn't lag a Firestore
  // round-trip per keystroke. Reset to canonical on row/exec swap.
  const [startDraft, setStartDraft] = useState<string>(
    toDatetimeLocalValue(canonicalStart),
  );
  const [endDraft, setEndDraft] = useState<string>(
    toDatetimeLocalValue(canonicalEnd),
  );
  const startTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const endTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startPendingRef = useRef<Date | null | undefined>(undefined);
  const endPendingRef = useRef<Date | null | undefined>(undefined);

  const flushStart = useCallback(() => {
    if (startTimerRef.current) {
      clearTimeout(startTimerRef.current);
      startTimerRef.current = null;
    }
    const pending = startPendingRef.current;
    startPendingRef.current = undefined;
    if (pending === undefined || !uid || !exec) return;
    void runSave(() =>
      setExecutionActualTime(
        getDb(),
        projectId,
        exec.runId,
        exec.taskId,
        uid,
        { actualStartAt: pending },
      ),
    );
  }, [exec, projectId, runSave, uid]);

  const flushEnd = useCallback(() => {
    if (endTimerRef.current) {
      clearTimeout(endTimerRef.current);
      endTimerRef.current = null;
    }
    const pending = endPendingRef.current;
    endPendingRef.current = undefined;
    if (pending === undefined || !uid || !exec) return;
    void runSave(() =>
      setExecutionActualTime(
        getDb(),
        projectId,
        exec.runId,
        exec.taskId,
        uid,
        { actualEndAt: pending },
      ),
    );
  }, [exec, projectId, runSave, uid]);

  // Row identity changes (different task / different exec under same
  // task): flush in-flight typing and reset local drafts.
  useEffect(() => {
    flushStart();
    flushEnd();
    setStartDraft(toDatetimeLocalValue(canonicalStart));
    setEndDraft(toDatetimeLocalValue(canonicalEnd));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    exec?.executionId,
    exec?.startedAt?.toMillis?.(),
    exec?.completedAt?.toMillis?.(),
  ]);

  useEffect(() => {
    return () => {
      flushStart();
      flushEnd();
    };
  }, [flushStart, flushEnd]);

  // Compute the duration off the live drafts when editable, so the user
  // gets immediate feedback as they pick times — even before debounce
  // flushes. Past-phase rows compute off the canonical timestamps.
  const startForDuration = canEdit
    ? startDraft
      ? new Date(startDraft)
      : null
    : canonicalStart;
  const endForDuration = canEdit
    ? endDraft
      ? new Date(endDraft)
      : null
    : canonicalEnd;

  const rowClass = cn(
    'border-t border-slate-100 align-top',
    isCurrent ? 'bg-sky-50/40' : '',
  );

  return (
    <tr
      data-testid="phase-actual-row"
      data-run-id={run.runId}
      data-is-current={isCurrent ? 'true' : 'false'}
      className={rowClass}
    >
      <td className="whitespace-nowrap px-2 py-1.5">
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-slate-800">{run.name}</span>
          {isCurrent ? (
            <span
              className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700"
              title="現在のフェーズ"
            >
              現在
            </span>
          ) : null}
        </div>
      </td>
      <td
        className="whitespace-nowrap px-2 py-1.5 tabular-nums text-slate-600"
        data-testid="phase-planned-dates"
      >
        {formatPlannedRange(exec?.plannedStartDate, exec?.plannedEndDate)}
      </td>
      <td className="whitespace-nowrap px-2 py-1.5">
        {canEdit && exec ? (
          <div className="flex items-center gap-1">
            <input
              type="datetime-local"
              data-testid="phase-actual-start-input"
              value={startDraft}
              className="rounded border border-slate-200 px-1.5 py-0.5 text-xs"
              onChange={(e) => {
                const raw = e.target.value;
                setStartDraft(raw);
                startPendingRef.current = raw ? new Date(raw) : null;
                if (startTimerRef.current) clearTimeout(startTimerRef.current);
                startTimerRef.current = setTimeout(
                  flushStart,
                  AUTOSAVE_DEBOUNCE_MS,
                );
              }}
              onBlur={flushStart}
            />
            <span className="text-slate-400">→</span>
            <input
              type="datetime-local"
              data-testid="phase-actual-end-input"
              value={endDraft}
              className="rounded border border-slate-200 px-1.5 py-0.5 text-xs"
              onChange={(e) => {
                const raw = e.target.value;
                setEndDraft(raw);
                endPendingRef.current = raw ? new Date(raw) : null;
                if (endTimerRef.current) clearTimeout(endTimerRef.current);
                endTimerRef.current = setTimeout(
                  flushEnd,
                  AUTOSAVE_DEBOUNCE_MS,
                );
              }}
              onBlur={flushEnd}
            />
          </div>
        ) : (
          <span className="tabular-nums text-slate-700">
            {formatPhaseWhen(canonicalStart)}{' '}
            <span className="text-slate-400">→</span>{' '}
            {formatPhaseWhen(canonicalEnd)}
          </span>
        )}
        {isCurrent && !exec ? (
          <div className="mt-0.5 text-[10px] text-slate-400">
            Execution 未生成 — このフェーズの実績入力は生成完了後に有効化されます
          </div>
        ) : null}
      </td>
      <td className="whitespace-nowrap px-2 py-1.5 tabular-nums text-slate-700">
        {formatPhaseDuration(startForDuration, endForDuration)}
      </td>
    </tr>
  );
}
